import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { EmailBlockData } from '../ModernEmailBuilder';
import { BlockRenderer, computePadding } from './BlockRenderer';
import { BLOCK_TYPES } from './BlockPalette';
import { createBlockFromType } from '../utils/blockFactory';
import {
  type InlineHtmlTag,
  resolveSelection,
  textContainsInlineHtml,
  wrapTextSelectionWithHtml,
} from '../utils/richTextFormatting';

type TextColorRange = {
  start: number;
  end: number;
  color: string;
};

const normalizeColorRanges = (
  ranges: TextColorRange[] | undefined,
  textLength: number
): TextColorRange[] => {
  if (!Array.isArray(ranges) || textLength <= 0) return [];
  const cleaned: TextColorRange[] = [];
  ranges.forEach((range) => {
    if (!range || typeof range.color !== 'string') return;
    const color = range.color.trim();
    if (!color) return;
    const rawStart = typeof range.start === 'number' ? range.start : 0;
    const rawEnd = typeof range.end === 'number' ? range.end : textLength;
    const start = Math.max(0, Math.min(rawStart, textLength));
    const end = Math.max(start, Math.min(rawEnd, textLength));
    if (end <= start) return;
    cleaned.push({ start, end, color });
  });
  cleaned.sort((a, b) => {
    if (a.start === b.start) {
      return a.end - b.end;
    }
    return a.start - b.start;
  });
  const merged: TextColorRange[] = [];
  cleaned.forEach((current) => {
    const last = merged[merged.length - 1];
    if (last && last.color === current.color && current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  });
  return merged;
};

const COLUMN_SUPPORTED_TYPES = [
  'text',
  'heading',
  'image',
  'button',
  'divider',
  'spacer',
  'video',
  'social',
];

// Drop zone between blocks in a column
const ColumnBlockDropZone: React.FC<{
  blockId: string;
  columnIndex: number;
  insertIndex: number;
}> = React.memo(({ blockId, columnIndex, insertIndex }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-block-drop-${blockId}-${columnIndex}-${insertIndex}`,
    data: {
      type: 'column-block',
      blockId: blockId,
      columnIndex: columnIndex,
      insertIndex: insertIndex,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`transition-all relative ${isOver
        ? 'h-3'
        : 'h-0 hover:h-2'
        }`}
    >
      {isOver ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-1 bg-teal-500 rounded-full shadow-lg" />
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
          <div className="w-full h-0.5 bg-teal-300 rounded-full" />
        </div>
      )}
    </div>
  );
});

ColumnBlockDropZone.displayName = 'ColumnBlockDropZone';

// ColumnDropZone component defined outside to prevent recreation issues
const ColumnDropZoneWrapper: React.FC<{
  blockId: string;
  columnIndex: number;
  columnContent: any;
  renderContent: (content: any, index: number) => React.ReactNode;
}> = React.memo(({ blockId, columnIndex, columnContent, renderContent }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-drop-${blockId}-${columnIndex}`,
    data: {
      type: 'column',
      blockId: blockId,
      columnIndex: columnIndex,
      insertIndex: -1, // -1 means append to end
    },
  });

  // Check if column has content (blocks)
  const hasContent = React.useMemo(() => {
    if (!columnContent) return false;
    if (Array.isArray(columnContent)) {
      return columnContent.length > 0;
    }
    if (typeof columnContent === 'object' && columnContent.type) {
      return true;
    }
    if (typeof columnContent === 'string' && columnContent !== '') {
      return true;
    }
    return false;
  }, [columnContent]);

  return (
    <div
      ref={setNodeRef}
      className={`rounded-none flex flex-col transition-colors ${hasContent
        ? 'min-h-0 p-0'
        : 'min-h-[240px] p-3 border border-dashed'
        } ${isOver
          ? 'border-teal-500 bg-teal-50'
          : hasContent
            ? ''
            : 'border-slate-300 bg-slate-50'
        }`}
    >
      <div className="flex-1 flex flex-col">{renderContent(columnContent, columnIndex)}</div>
    </div>
  );
});

ColumnDropZoneWrapper.displayName = 'ColumnDropZoneWrapper';

const DEFAULT_HEADING_EMOJIS = [
  '😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😇', '😊', '🙂',
  '🙃', '😉', '😍', '😘', '🥰', '🤩', '😎', '🤓', '🧐', '🤔',
  '🤗', '🤝', '🙏', '🙌', '👏', '👍', '💪', '🤯', '🤠', '😺',
  '😻', '🎉', '🎊', '🎯', '🏆', '🥇', '🚀', '🛠️', '💡', '💥',
  '🔥', '✨', '🌟', '🌈', '💬', '📣', '📈', '💻', '🧠', '📝',
  '📅', '📎', '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🤎',
];
const HEADING_EMOJI_MAP: Record<string, string[]> = {
  smiles: ['😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😇', '😊', '🙂', '🙃', '😉', '😍', '😘', '🥰', '🤩', '😎'],
  gestures: ['🤓', '🧐', '🤔', '🤗', '🤝', '🙏', '🙌', '👏', '👍', '💪', '🤠'],
  celebrate: ['😺', '😻', '🎉', '🎊', '🎯', '🏆', '🥇', '🚀', '🛠️', '💡', '💥', '🔥', '✨', '🌟', '🌈'],
  work: ['💬', '📣', '📈', '💻', '🧠', '📝', '📅', '📎'],
  hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🤎'],
};

const DEFAULT_HEADING_MERGE_TAGS: { label: string; mergeTag: string }[] = [
  { label: 'First Name', mergeTag: '{{first_name}}' },
  { label: 'Last Name', mergeTag: '{{last_name}}' },
  { label: 'Email', mergeTag: '{{email}}' },
  { label: 'Company', mergeTag: '{{company}}' },
];

interface EmailBlockProps {
  block: EmailBlockData;
  isSelected: boolean;
  isPreview: boolean;
  onSelect: () => void;
  onUpdate: (blockId: string, updates: Partial<EmailBlockData>) => void;
  onDelete: (blockId: string) => void;
  onDuplicate: (blockId: string) => void;
  onMoveUp?: (blockId: string) => void;
  onMoveDown?: (blockId: string) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onInsertBlock?: (blockId: string, blockType: string, position: 'above' | 'below') => void;
  blockIndex?: number;
  onSelectColumnBlock?: (blockId: string, columnIndex: number, blockIndex: number) => void;
  onDeleteColumnBlock?: (blockId: string, columnIndex: number, blockIndex: number) => void;
  selectedColumnBlock?: { parentId: string; columnIndex: number; blockIndex: number } | null;
  onUpdateColumnBlock?: (parentId: string, columnIndex: number, blockIndex: number, updates: Partial<EmailBlockData>) => void;
  onSaveBlock?: (block: EmailBlockData, name: string) => void;
  isMobile?: boolean;
  isLocked?: boolean; // When true, block cannot be deleted (e.g. Scubamail branding on free plan)
  hasValidationError?: boolean;
  globalFontFamily?: string;
}

export const EmailBlock: React.FC<EmailBlockProps> = ({
  block,
  isSelected,
  isPreview,
  onSelect,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
  onInsertBlock,
  blockIndex,
  onSaveBlock,
  onSelectColumnBlock,
  onDeleteColumnBlock,
  selectedColumnBlock,
  onUpdateColumnBlock,
  isMobile = false,
  isLocked = false,
  hasValidationError = false,
  globalFontFamily,
}) => {
  const [showImageInput, setShowImageInput] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [imageUrl, setImageUrl] = useState(block.content.src || '');
  const [linkUrl, setLinkUrl] = useState(block.content.linkUrl || '');
  const [controlBarPosition, setControlBarPosition] = useState<{ top: number; right: number } | null>(null);
  const [showComponentPicker, setShowComponentPicker] = useState<'top' | 'bottom' | null>(null);
  const [pickerPosition, setPickerPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDraggingPicker, setIsDraggingPicker] = useState(false);
  const pickerDragOffset = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const blockRef = React.useRef<HTMLDivElement>(null);
  const pickerRef = React.useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const contextMenuRef = React.useRef<HTMLDivElement>(null);

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu]);

  const normalizedBlockType = block.type === 'paragraph' ? 'text' : block.type;
  const isImageBlock = block.type === 'image';
  const isColumnsBlock = block.type === 'columns';
  const isHeadingBlock = block.type === 'heading' || block.type === 'title' || block.type === 'subtitle';
  const isTextBlock = normalizedBlockType === 'text' || block.type === 'caption' || block.type === 'preheader';
  const isQuoteBlock = block.type === 'quote';
  const isListBlock = block.type === 'list';
  const isRichTextBlock = isHeadingBlock || isTextBlock || isQuoteBlock;
  const [isInlineEditingHeading, setIsInlineEditingHeading] = useState(false);
  const [inlineHeadingValue, setInlineHeadingValue] = useState(block.content.text || '');
  const inlineHeadingTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [showHeadingEmojiDropdown, setShowHeadingEmojiDropdown] = useState(false);
  const [headingEmojiSearch, setHeadingEmojiSearch] = useState('');
  const filteredHeadingEmojis = useMemo(() => {
    const query = headingEmojiSearch.trim();
    if (!query) return DEFAULT_HEADING_EMOJIS;
    const regex = new RegExp(query, 'i');
    const result = new Set<string>();
    Object.entries(HEADING_EMOJI_MAP).forEach(([category, emojis]) => {
      if (regex.test(category)) {
        emojis.forEach((emoji) => result.add(emoji));
      }
    });
    DEFAULT_HEADING_EMOJIS.forEach((emoji) => {
      if (regex.test(emoji)) {
        result.add(emoji);
      }
    });
    return Array.from(result);
  }, [headingEmojiSearch]);
  const [showHeadingMergeDropdown, setShowHeadingMergeDropdown] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [customTextColor, setCustomTextColor] = useState('#111827');
  const [showHeadingLinkModal, setShowHeadingLinkModal] = useState(false);
  const [headingLinkForm, setHeadingLinkForm] = useState<{ url: string; target: '_blank' | '_self' }>({
    url: '',
    target: '_blank',
  });
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [saveBlockName, setSaveBlockName] = useState('');
  const savePopupRef = React.useRef<HTMLDivElement | null>(null);
  const headingToolbarRef = React.useRef<HTMLDivElement | null>(null);
  const headingLinkModalRef = React.useRef<HTMLDivElement | null>(null);
  const pendingInlineFormatRef = React.useRef<InlineHtmlTag | null>(null);
  const headingSelectionRangeRef = React.useRef<{ start: number; end: number }>({
    start: 0,
    end: (block.content.text || '').length,
  });

  const scaleFontSize = (value: string | number | undefined, factor: number) => {
    if (typeof value === 'number') {
      return `${value * factor}px`;
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!Number.isNaN(parsed)) {
        const numericPart = parsed.toString();
        const unit = value.slice(numericPart.length) || 'px';
        return `${parsed * factor}${unit}`;
      }
    }
    return value;
  };

  const updateHeadingContent = useCallback(
    (updates: Record<string, any>) => {
      const nextContent = {
        ...block.content,
        ...updates,
      };
      const resolvedText =
        typeof nextContent.text === 'string'
          ? nextContent.text
          : block.content.text || (isHeadingBlock ? 'Heading' : 'Text content');
      if (nextContent.colorRanges) {
        const normalized = normalizeColorRanges(nextContent.colorRanges, resolvedText.length);
        if (normalized.length) {
          nextContent.colorRanges = normalized;
        } else {
          delete nextContent.colorRanges;
        }
      }
      onUpdate(block.id, {
        content: nextContent,
      });
    },
    [block.content, block.id, onUpdate, isHeadingBlock]
  );

  const updateHeadingStyles = useCallback(
    (updates: Record<string, any>) => {
      const nextStyles = { ...block.styles };
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          delete nextStyles[key];
        } else {
          nextStyles[key] = value;
        }
      });

      onUpdate(block.id, {
        styles: nextStyles,
      });
    },
    [block.styles, block.id, onUpdate]
  );

  const headingFontWeightValue = block.styles.fontWeight?.toString() || '700';
  const headingFontWeightNumber = parseInt(headingFontWeightValue, 10);
  const isHeadingBold = Number.isNaN(headingFontWeightNumber)
    ? headingFontWeightValue === 'bold'
    : headingFontWeightNumber >= 600;
  const isHeadingItalic = (block.styles.fontStyle || 'normal') === 'italic';
  const headingTextDecoration = (block.styles.textDecoration as string) || '';
  const headingDecorations = useMemo(
    () =>
      new Set(
        headingTextDecoration
          .split(/\s+/)
          .map((value) => value.trim())
          .filter((value) => value && value !== 'none')
      ),
    [headingTextDecoration]
  );
  const hasHeadingUnderline = headingDecorations.has('underline');
  const hasHeadingStrikethrough = headingDecorations.has('line-through');
  const headingPosition = block.styles.headingPosition || 'normal';
  const isHeadingSuperscript = headingPosition === 'superscript';
  const isHeadingSubscript = headingPosition === 'subscript';
  const headingTextValue =
    inlineHeadingValue || block.content.text || (isHeadingBlock ? 'Heading' : 'Text content');
  const headingColorRanges = useMemo(
    () => normalizeColorRanges(block.content.colorRanges as TextColorRange[] | undefined, headingTextValue.length),
    [block.content.colorRanges, headingTextValue.length]
  );
  const getResolvedHeadingLinkRange = (text: string) => {
    const rawStart =
      typeof block.content.linkRangeStart === 'number' ? block.content.linkRangeStart : 0;
    const rawEnd =
      typeof block.content.linkRangeEnd === 'number' ? block.content.linkRangeEnd : text.length;
    const start = Math.max(0, Math.min(rawStart, text.length));
    const end = Math.max(start, Math.min(rawEnd, text.length));
    return { start, end };
  };
  const { start: headingLinkRangeStart, end: headingLinkRangeEnd } =
    getResolvedHeadingLinkRange(headingTextValue);
  const headingHasLink = Boolean(
    block.content.linkUrl &&
    block.content.linkUrl.trim() !== '' &&
    headingLinkRangeEnd > headingLinkRangeStart
  );
  const headingLinkTarget = block.content.linkTarget || '_blank';
  const isHeadingLinkFormValid = headingLinkForm.url.trim().length > 0;

  const toggleHeadingBold = useCallback(() => {
    updateHeadingStyles({ fontWeight: isHeadingBold ? '400' : '700' });
  }, [isHeadingBold, updateHeadingStyles]);

  const toggleHeadingItalic = useCallback(() => {
    updateHeadingStyles({ fontStyle: isHeadingItalic ? undefined : 'italic' });
  }, [isHeadingItalic, updateHeadingStyles]);

  const toggleHeadingDecoration = useCallback(
    (decoration: 'underline' | 'line-through') => {
      const current = new Set(
        ((block.styles.textDecoration as string) || '')
          .split(/\s+/)
          .map((value) => value.trim())
          .filter((value) => value && value !== 'none')
      );

      if (current.has(decoration)) {
        current.delete(decoration);
      } else {
        current.add(decoration);
      }

      const nextValue = current.size ? Array.from(current).join(' ') : undefined;
      updateHeadingStyles({ textDecoration: nextValue });
    },
    [block.styles.textDecoration, updateHeadingStyles]
  );

  const toggleTextListType = useCallback(
    (listType: 'unordered' | 'ordered' | 'check') => {
      const nextValue = block.content.listType === listType ? undefined : listType;
      updateHeadingContent({ listType: nextValue });
    },
    [block.content.listType, updateHeadingContent]
  );

  const addListItem = useCallback(() => {
    const currentItems = Array.isArray(block.content.items) ? [...block.content.items] : (block.content.text ? block.content.text.split('\n') : ['List item 1']);
    updateHeadingContent({ items: [...currentItems, `List item ${currentItems.length + 1}`] });
  }, [block.content.items, block.content.text, updateHeadingContent]);

  const removeListItem = useCallback((index: number) => {
    const currentItems = Array.isArray(block.content.items) ? [...block.content.items] : (block.content.text ? block.content.text.split('\n') : ['List item 1']);
    if (currentItems.length <= 1) return;
    currentItems.splice(index, 1);
    updateHeadingContent({ items: currentItems });
  }, [block.content.items, block.content.text, updateHeadingContent]);

  const updateListItem = useCallback((index: number, value: string) => {
    const currentItems = Array.isArray(block.content.items) ? [...block.content.items] : (block.content.text ? block.content.text.split('\n') : ['List item 1']);
    currentItems[index] = value;
    updateHeadingContent({ items: currentItems });
  }, [block.content.items, block.content.text, updateHeadingContent]);

  const insertHeadingEmoji = useCallback(
    (emoji: string) => {
      const baseText = inlineHeadingValue || block.content.text || 'Heading';
      const newText = `${baseText}${emoji}`;
      setInlineHeadingValue(newText);
      updateHeadingContent({ text: newText });
      setShowHeadingEmojiDropdown(false);
    },
    [block.content.text, inlineHeadingValue, updateHeadingContent]
  );

  const insertHeadingMergeTag = useCallback(
    (mergeTag: string) => {
      const baseText = inlineHeadingValue || block.content.text || 'Heading';
      const newText = `${baseText}${mergeTag}`;
      setInlineHeadingValue(newText);
      updateHeadingContent({ text: newText });
      setShowHeadingMergeDropdown(false);
    },
    [block.content.text, inlineHeadingValue, updateHeadingContent]
  );

  const applyInlineTextColor = useCallback(
    (color?: string | null) => {
      if (!isRichTextBlock) return;
      const textValue = headingTextValue;
      const textLength = textValue.length;
      if (!textLength) return;
      // Capture latest selection from textarea if available
      if (inlineHeadingTextareaRef.current) {
        const textarea = inlineHeadingTextareaRef.current;
        headingSelectionRangeRef.current = {
          start: textarea.selectionStart ?? headingSelectionRangeRef.current.start ?? 0,
          end: textarea.selectionEnd ?? headingSelectionRangeRef.current.end ?? 0,
        };
      }
      let { start, end } = headingSelectionRangeRef.current;
      start = Math.max(0, Math.min(start, textLength));
      end = Math.max(start, Math.min(end, textLength));
      if (start === end) {
        return;
      }
      const currentRanges = normalizeColorRanges(
        block.content.colorRanges as TextColorRange[] | undefined,
        textLength
      );
      const updated: TextColorRange[] = [];
      currentRanges.forEach((range) => {
        if (range.end <= start || range.start >= end) {
          updated.push(range);
        } else {
          if (range.start < start) {
            updated.push({ ...range, end: start });
          }
          if (range.end > end) {
            updated.push({ ...range, start: end });
          }
        }
      });
      const nextColor = color && color.trim();
      if (nextColor) {
        updated.push({ start, end, color: nextColor });
      }
      const merged = normalizeColorRanges(updated, textLength);
      updateHeadingContent({
        colorRanges: merged.length ? merged : undefined,
      });
      setShowTextColorPicker(false);
    },
    [block.content.colorRanges, headingTextValue, isRichTextBlock, updateHeadingContent]
  );

  const handleHeadingLinkPrompt = useCallback(() => {
    const textLength = headingTextValue.length;
    const textarea = inlineHeadingTextareaRef.current;
    const currentSelectionStart =
      textarea?.selectionStart ?? headingSelectionRangeRef.current.start ?? 0;
    const currentSelectionEnd =
      textarea?.selectionEnd ?? headingSelectionRangeRef.current.end ?? 0;
    let selectionStart = currentSelectionStart;
    let selectionEnd = currentSelectionEnd;

    if (selectionStart === selectionEnd) {
      if (headingHasLink) {
        selectionStart = headingLinkRangeStart;
        selectionEnd = headingLinkRangeEnd;
      } else {
        selectionStart = 0;
        selectionEnd = textLength;
      }
    }

    headingSelectionRangeRef.current = {
      start: Math.max(0, Math.min(selectionStart, textLength)),
      end: Math.max(0, Math.min(selectionEnd, textLength)),
    };

    setHeadingLinkForm({
      url: block.content.linkUrl || '',
      target: block.content.linkTarget === '_self' ? '_self' : '_blank',
    });
    setShowHeadingLinkModal(true);
  }, [
    block.content.linkTarget,
    block.content.linkUrl,
    headingHasLink,
    headingLinkRangeEnd,
    headingLinkRangeStart,
    headingTextValue,
  ]);

  const handleHeadingLinkSave = useCallback(() => {
    const trimmedUrl = headingLinkForm.url.trim();
    if (!trimmedUrl) {
      updateHeadingContent({ linkUrl: '', linkTarget: undefined, linkRangeStart: undefined, linkRangeEnd: undefined });
      setShowHeadingLinkModal(false);
      return;
    }
    const textLength = headingTextValue.length;
    let { start, end } = headingSelectionRangeRef.current;
    start = Math.max(0, Math.min(start, textLength));
    end = Math.max(start, Math.min(end, textLength));
    if (start === end) {
      start = 0;
      end = textLength;
    }
    updateHeadingContent({
      linkUrl: trimmedUrl,
      linkTarget: headingLinkForm.target,
      linkRangeStart: start,
      linkRangeEnd: end,
    });
    setShowHeadingLinkModal(false);
  }, [headingLinkForm, headingTextValue.length, updateHeadingContent]);

  const handleRemoveHeadingLink = useCallback(() => {
    if (!headingHasLink) return;
    updateHeadingContent({
      linkUrl: '',
      linkTarget: undefined,
      linkRangeStart: undefined,
      linkRangeEnd: undefined,
    });
    headingSelectionRangeRef.current = { start: 0, end: 0 };
  }, [headingHasLink, updateHeadingContent]);

  const handleHeadingLinkFormChange = useCallback((field: 'url' | 'target', value: string) => {
    setHeadingLinkForm((prev) => ({
      ...prev,
      [field]: field === 'target' ? (value === '_self' ? '_self' : '_blank') : value,
    }));
  }, []);

  const adjustInlineTextareaHeight = useCallback(() => {
    const textarea = inlineHeadingTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, []);

  // Sync state when block content changes
  useEffect(() => {
    setImageUrl(block.content.src || '');
    setLinkUrl(block.content.linkUrl || '');
  }, [block.content.src, block.content.linkUrl]);

  useEffect(() => {
    const text = block.content.text || '';
    setInlineHeadingValue(text);
    headingSelectionRangeRef.current = {
      start: 0,
      end: text.length,
    };
  }, [block.content.text]);

  useEffect(() => {
    if (isInlineEditingHeading && inlineHeadingTextareaRef.current) {
      const textarea = inlineHeadingTextareaRef.current;
      textarea.focus();
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
      adjustInlineTextareaHeight();
    }
  }, [isInlineEditingHeading, adjustInlineTextareaHeight]);

  useEffect(() => {
    if (!isInlineEditingHeading) return;
    adjustInlineTextareaHeight();
  }, [inlineHeadingValue, isInlineEditingHeading, adjustInlineTextareaHeight]);

  const commitHeadingChange = useCallback(() => {
    if (!isRichTextBlock) return;
    const rawValue = inlineHeadingValue ?? '';
    // IMMEDIATELY trim all whitespace - no exceptions, no complex regex, just trim()
    const finalText = rawValue.trim() || (isHeadingBlock ? 'Heading' : 'Text content');
    const nextContent: Record<string, unknown> = {
      ...block.content,
      text: finalText,
    };
    if (textContainsInlineHtml(finalText)) {
      nextContent.isHtml = true;
    } else if (block.content.isHtml) {
      delete nextContent.isHtml;
    }
    onUpdate(block.id, { content: nextContent });
    setIsInlineEditingHeading(false);
  }, [inlineHeadingValue, isHeadingBlock, isRichTextBlock, onUpdate, block.content, block.id]);

  const applyInlineHtmlFormat = useCallback(
    (tagName: InlineHtmlTag) => {
      const textarea = inlineHeadingTextareaRef.current;
      const liveStart = textarea?.selectionStart ?? headingSelectionRangeRef.current.start;
      const liveEnd = textarea?.selectionEnd ?? headingSelectionRangeRef.current.end;
      const { start, end } = resolveSelection(liveStart, liveEnd, headingSelectionRangeRef.current);
      const result = wrapTextSelectionWithHtml(inlineHeadingValue, tagName, { start, end });
      setInlineHeadingValue(result.text);
      updateHeadingContent({ text: result.text, isHtml: true });
      headingSelectionRangeRef.current = result.selection;
      requestAnimationFrame(() => {
        const input = inlineHeadingTextareaRef.current;
        if (!input) return;
        input.focus();
        input.setSelectionRange(result.selection.start, result.selection.end);
      });
    },
    [inlineHeadingValue, updateHeadingContent]
  );

  const requestInlineHtmlFormat = useCallback(
    (tagName: InlineHtmlTag) => {
      if (!isTextBlock) return;
      if (!isInlineEditingHeading) {
        pendingInlineFormatRef.current = tagName;
        setIsInlineEditingHeading(true);
        return;
      }
      applyInlineHtmlFormat(tagName);
    },
    [applyInlineHtmlFormat, isInlineEditingHeading, isTextBlock]
  );

  const handleRichTextToolbarMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (!isInlineEditingHeading || !pendingInlineFormatRef.current) return;
    const tag = pendingInlineFormatRef.current;
    const frame = requestAnimationFrame(() => {
      if (!inlineHeadingTextareaRef.current) return;
      pendingInlineFormatRef.current = null;
      applyInlineHtmlFormat(tag);
    });
    return () => cancelAnimationFrame(frame);
  }, [isInlineEditingHeading, applyInlineHtmlFormat]);

  const cancelHeadingChange = useCallback(() => {
    if (!isRichTextBlock) return;
    setInlineHeadingValue(block.content.text || '');
    setIsInlineEditingHeading(false);
  }, [block.content.text, isRichTextBlock]);

  const handleHeadingClick = useCallback(
    (event: React.MouseEvent) => {
      if (!isRichTextBlock || isInlineEditingHeading || isLocked) return;
      event.stopPropagation();
      if (!isSelected) {
        onSelect();
      }
      setIsInlineEditingHeading(true);
    },
    [isInlineEditingHeading, isRichTextBlock, isSelected, onSelect, isLocked]
  );

  const handleHeadingKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Allow Enter to create a new line in all text/heading blocks.
      // Committing changes can be done by clicking outside (onBlur) or pressing Escape.
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelHeadingChange();
    }
  };

  // Close picker when clicking outside or when block is deselected
  useEffect(() => {
    if (!isSelected) {
      setShowComponentPicker(null);
      setShowHeadingEmojiDropdown(false);
      setShowHeadingMergeDropdown(false);
      setShowHeadingLinkModal(false);
      if (isInlineEditingHeading) {
        cancelHeadingChange();
      }
    }
  }, [isSelected, isInlineEditingHeading, cancelHeadingChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        pickerRef.current &&
        !pickerRef.current.contains(target) &&
        blockRef.current &&
        !blockRef.current.contains(target)
      ) {
        setShowComponentPicker(null);
      }
    };

    if (showComponentPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showComponentPicker]);


  useEffect(() => {
    if (!showHeadingLinkModal) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        headingLinkModalRef.current &&
        !headingLinkModalRef.current.contains(event.target as Node)
      ) {
        setShowHeadingLinkModal(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowHeadingLinkModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showHeadingLinkModal]);

  // Handle click outside save popup
  useEffect(() => {
    if (!showSavePopup) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        savePopupRef.current &&
        !savePopupRef.current.contains(event.target as Node)
      ) {
        setShowSavePopup(false);
        setSaveBlockName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSavePopup]);

  useEffect(() => {
    if (!showHeadingEmojiDropdown && !showHeadingMergeDropdown && !showTextColorPicker) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        headingToolbarRef.current &&
        !headingToolbarRef.current.contains(event.target as Node)
      ) {
        setShowHeadingEmojiDropdown(false);
        setHeadingEmojiSearch('');
        setShowHeadingMergeDropdown(false);
        setShowTextColorPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHeadingEmojiDropdown, showHeadingMergeDropdown, showTextColorPicker]);

  // Calculate control bar position when block is selected
  useEffect(() => {
    if (isSelected && isImageBlock && blockRef.current) {
      const updatePosition = () => {
        if (blockRef.current) {
          const rect = blockRef.current.getBoundingClientRect();
          const canvasContainer = blockRef.current.closest('.email-canvas') || blockRef.current.closest('main');
          if (canvasContainer) {
            const containerRect = canvasContainer.getBoundingClientRect();
            // Position to the right of the canvas container
            setControlBarPosition({
              top: rect.top + window.scrollY,
              right: window.innerWidth - containerRect.right + 16, // 16px gap from canvas edge
            });
          } else {
            // Fallback: position relative to viewport
            setControlBarPosition({
              top: rect.top + window.scrollY,
              right: 16,
            });
          }
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setControlBarPosition(null);
    }
  }, [isSelected, isImageBlock]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id, disabled: isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleAddImage = () => {
    if (imageUrl.trim()) {
      onUpdate(block.id, {
        content: {
          ...block.content,
          src: imageUrl.trim(),
        },
      });
    }
    setShowImageInput(false);
  };

  const handleAddLink = () => {
    onUpdate(block.id, {
      content: {
        ...block.content,
        linkUrl: linkUrl.trim(),
      },
    });
    setShowLinkInput(false);
  };


  const createColumnBlockContent = (type: string) => {
    const baseBlock = createBlockFromType(type);
    return {
      type: baseBlock.type,
      content: baseBlock.content,
      styles: baseBlock.styles,
    };
  };

  const handleAddBlockToColumn = (columnIndex: number, blockType: string, insertIndex?: number) => {
    const newColumnBlock = createColumnBlockContent(blockType);
    const inferredColumns =
      block.content.columns ||
      block.content.columnWidths?.length ||
      (Array.isArray(block.content.columnContent) ? block.content.columnContent.length : 0) ||
      2;

    const updatedColumns = Array.from(
      { length: inferredColumns },
      (_, idx) => {
        const existing = Array.isArray(block.content.columnContent) ? block.content.columnContent[idx] : '';
        // Normalize to array format
        if (!existing || existing === '') {
          return [];
        } else if (Array.isArray(existing)) {
          return [...existing];
        } else if (typeof existing === 'object' && existing.type) {
          return [existing];
        }
        return [];
      }
    );

    // Insert or append the new block
    if (typeof insertIndex === 'number' && insertIndex >= 0) {
      updatedColumns[columnIndex] = [...updatedColumns[columnIndex]];
      updatedColumns[columnIndex].splice(insertIndex, 0, newColumnBlock);
    } else {
      // Append to end
      updatedColumns[columnIndex] = [...updatedColumns[columnIndex], newColumnBlock];
    }

    onUpdate(block.id, {
      content: {
        ...block.content,
        columnContent: updatedColumns,
      },
    });
    // Select the newly added block (it will be at the end of the array or at insertIndex)
    const newBlockIndex = typeof insertIndex === 'number' && insertIndex >= 0
      ? insertIndex
      : updatedColumns[columnIndex].length - 1;
    onSelectColumnBlock?.(block.id, columnIndex, newBlockIndex);
  };

  const handleDeleteColumnBlock = (columnIndex: number, blockIndex: number) => {
    if (!Array.isArray(block.content.columnContent)) return;

    const updatedColumns = Array.from(
      { length: block.content.columnContent.length },
      (_, idx) => {
        const existing = block.content.columnContent[idx];
        if (idx === columnIndex) {
          // Normalize to array
          let blocks: any[] = [];
          if (Array.isArray(existing)) {
            blocks = [...existing];
          } else if (existing && existing !== '' && typeof existing === 'object' && existing.type) {
            blocks = [existing];
          }
          // Remove the block at blockIndex
          blocks.splice(blockIndex, 1);
          return blocks.length > 0 ? blocks : [];
        }
        // Normalize other columns
        if (!existing || existing === '') {
          return [];
        } else if (Array.isArray(existing)) {
          return [...existing];
        } else if (typeof existing === 'object' && existing.type) {
          return [existing];
        }
        return [];
      }
    );

    onUpdate(block.id, {
      content: {
        ...block.content,
        columnContent: updatedColumns,
      },
    });
  };

  const handleDeleteColumnContent = (columnIndex: number) => {
    if (!Array.isArray(block.content.columnContent)) return;

    const columns = [...block.content.columnContent];
    columns[columnIndex] = []; // Clear all blocks in this column (set to empty array)

    const columnCount =
      columns.length ||
      block.content.columns ||
      block.content.columnWidths?.length ||
      1;

    let columnWidths = block.content.columnWidths
      ? [...block.content.columnWidths]
      : Array.from({ length: columnCount }, () => `${(100 / columnCount).toFixed(2)}%`);

    if (columns.length === 1) {
      columnWidths = ['100%'];
    } else {
      columnWidths = Array.from({ length: columns.length }, () => `${(100 / columns.length).toFixed(2)}%`);
    }

    onUpdate(block.id, {
      content: {
        ...block.content,
        columns: columns.length,
        columnContent: columns,
        columnWidths,
      },
    });
    // handleDeleteColumnContent clears all blocks in a column, so we don't need to call onDeleteColumnBlock
    // The column is now empty, so there's no specific block to delete
  };

  if (block.hidden) {
    return null;
  }

  if (isPreview) {
    return (
      <div className="email-block-preview">
        <BlockRenderer block={block} isMobile={isMobile} globalFontFamily={globalFontFamily} />
      </div>
    );
  }

  const getColumnCount = () =>
    block.content.columns ||
    block.content.columnWidths?.length ||
    (Array.isArray(block.content.columnContent) ? block.content.columnContent.length : 0) ||
    2;

  const renderColumnContentPreview = (columnItem: any, columnIndex: number) => {
    const totalColumns = getColumnCount();

    // Normalize columnItem to array format
    let columnBlocks: any[] = [];
    if (Array.isArray(columnItem)) {
      columnBlocks = columnItem.filter(item => item && item !== '');
    } else if (columnItem && columnItem !== '' && (typeof columnItem === 'object' && columnItem.type)) {
      // Single block object - convert to array
      columnBlocks = [columnItem];
    } else if (typeof columnItem === 'string' && columnItem !== '') {
      // Legacy string format - skip for now
      columnBlocks = [];
    }

    // Calculate max blocks across all columns for alignment
    const allColumns = Array.from({ length: totalColumns }, (_, idx) => {
      const col = Array.isArray(block.content.columnContent) ? block.content.columnContent[idx] : '';
      if (Array.isArray(col)) {
        return col.filter(item => item && item !== '').length;
      }
      return col && col !== '' ? 1 : 0;
    });
    const maxBlocks = Math.max(...allColumns, 1);

    // Empty column
    if (columnBlocks.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-center text-xs text-slate-500 gap-2 relative" style={{ minHeight: '160px' }}>
          {isSelected && totalColumns > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteColumnContent(columnIndex);
              }}
              className="absolute top-2 right-2 bg-white border border-slate-300 rounded-full p-1 text-slate-600 hover:bg-slate-100"
              style={{ boxShadow: '0 2px 6px rgba(15, 23, 42, 0.15)' }}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          <div className="flex flex-col items-center justify-center text-center text-xs text-slate-500 gap-1">
            <div className="text-sm font-medium text-slate-600">Drag content here</div>
          </div>
        </div>
      );
    }

    // Render multiple blocks with drop zones between them, aligned by row
    return (
      <div className="flex-1 flex flex-col" style={{ height: '100%', gap: 0 }}>
        {Array.from({ length: maxBlocks }).map((_, blockIndex) => {
          const blockItem = columnBlocks[blockIndex];
          const hasBlock = !!blockItem;

          return (
            <React.Fragment key={blockIndex}>
              {hasBlock && (
                <ColumnBlockDropZone
                  blockId={block.id}
                  columnIndex={columnIndex}
                  insertIndex={blockIndex}
                />
              )}
              <div
                className={`relative w-full min-w-0 cursor-pointer group m-0 p-0 ${hasBlock ? 'min-h-[80px]' : 'min-h-0'}`}
                style={{ flexShrink: 0, flexGrow: hasBlock ? 0 : 1 }}
                onClick={(e) => {
                  if (hasBlock) {
                    e.stopPropagation();
                    onSelectColumnBlock?.(block.id, columnIndex, blockIndex);
                  }
                }}
              >
                {hasBlock ? (
                  <>
                    {/* Unified Column Precision Border Overlay */}
                    <div 
                      className={`absolute inset-0 pointer-events-none rounded-none transition-colors z-10 ${
                        (selectedColumnBlock?.parentId === block.id &&
                         selectedColumnBlock?.columnIndex === columnIndex &&
                         selectedColumnBlock?.blockIndex === blockIndex)
                          ? 'border-2 border-blue-500' 
                          : 'border-2 border-dashed border-transparent group-hover:border-blue-400'
                      }`} 
                    />
                    {/* Selection label */}
                    {selectedColumnBlock?.parentId === block.id &&
                      selectedColumnBlock?.columnIndex === columnIndex &&
                      selectedColumnBlock?.blockIndex === blockIndex && (
                        <div
                          className="absolute bg-blue-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-lg z-20 whitespace-nowrap"
                          style={{
                            top: '-12px',
                            left: '8px',
                          }}
                        >
                          {BLOCK_TYPES.find(bt => bt.type === blockItem.type)?.label || blockItem.type}
                        </div>
                      )}
                    <BlockRenderer
                      block={{
                        id: `${block.id}-column-${columnIndex}-block-${blockIndex}`,
                        type: blockItem.type,
                        content: blockItem.content || {},
                        styles: blockItem.styles || {},
                      }}
                      isEditable={
                        selectedColumnBlock?.parentId === block.id &&
                        selectedColumnBlock?.columnIndex === columnIndex &&
                        selectedColumnBlock?.blockIndex === blockIndex &&
                        blockItem.type === 'text'
                      }
                      isSelected={
                        selectedColumnBlock?.parentId === block.id &&
                        selectedColumnBlock?.columnIndex === columnIndex &&
                        selectedColumnBlock?.blockIndex === blockIndex
                      }
                      isMobile={isMobile}
                      onTextUpdate={(newText) => {
                        if (onUpdateColumnBlock && blockItem.type === 'text') {
                          onUpdateColumnBlock(block.id, columnIndex, blockIndex, {
                            content: { ...blockItem.content, text: newText },
                          });
                        }
                      }}
                    />
                    {isSelected && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteColumnBlock(columnIndex, blockIndex);
                        }}
                        className="absolute top-2 right-2 bg-white border border-slate-300 rounded-full p-1 text-slate-600 hover:bg-slate-100 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                        style={{ boxShadow: '0 2px 6px rgba(15, 23, 42, 0.15)' }}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </>
                ) : (
                  <div style={{ width: '100%', minHeight: '0px' }} />
                )}
              </div>
            </React.Fragment>
          );
        })}
        {/* Drop zone at the end */}
        <ColumnBlockDropZone
          blockId={block.id}
          columnIndex={columnIndex}
          insertIndex={columnBlocks.length}
        />
      </div>
    );
  };

  const renderColumnsEditor = () => {
    const columnCount =
      block.content.columns ||
      block.content.columnWidths?.length ||
      (Array.isArray(block.content.columnContent) ? block.content.columnContent.length : 0) ||
      2;

    const columnWidths =
      block.content.columnWidths ||
      Array.from({ length: columnCount }, () => `${(100 / columnCount).toFixed(2)}%`);
    const hasCustomWidths = columnWidths.length === columnCount;
    const columnContent = Array.from(
      { length: columnCount },
      (_, idx) => {
        const existing = Array.isArray(block.content.columnContent) ? block.content.columnContent[idx] : '';
        // Normalize to array format
        if (!existing || existing === '') {
          return [];
        } else if (Array.isArray(existing)) {
          return existing;
        } else if (typeof existing === 'object' && existing.type) {
          return [existing];
        }
        return [];
      }
    );

    const gapSize = hasCustomWidths ? 12 : 16;
    const gapAdjustment =
      hasCustomWidths && columnCount > 1 ? (gapSize * (columnCount - 1)) / columnCount : 0;

    // Calculate max blocks across all columns for grid alignment
    const maxBlocks = Math.max(
      ...columnContent.map((col: any) => {
        if (Array.isArray(col)) {
          return col.filter((item: any) => item && item !== '').length;
        }
        return col && col !== '' ? 1 : 0;
      }),
      1
    );

    // In mobile mode, use flexbox to stack columns vertically
    if (isMobile) {
      return (
        <div
          style={{
            padding: computePadding(block.styles, '20px'),
            display: 'flex',
            flexDirection: 'column',
            gap: `${gapSize}px`,
            width: '100%',
            boxSizing: 'border-box',
            backgroundColor: block.styles.backgroundColor || 'transparent',
          }}
        >
          {Array.from({ length: columnCount }).map((_, index) => {
            return (
              <div
                key={index}
                data-column-index={index}
                className="relative flex flex-col"
                style={{
                  boxSizing: 'border-box',
                  width: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                }}
              >
                <ColumnDropZoneWrapper
                  blockId={block.id}
                  columnIndex={index}
                  columnContent={columnContent[index]}
                  renderContent={renderColumnContentPreview}
                />
              </div>
            );
          })}
        </div>
      );
    }

    // Desktop: use grid for horizontal layout
    return (
      <div
        style={{
          padding: computePadding(block.styles, '20px'),
          display: 'grid',
          gridTemplateColumns: Array.from({ length: columnCount })
            .map((_, idx) => {
              const width = hasCustomWidths ? columnWidths[idx] : `${(100 / columnCount).toFixed(2)}%`;
              return hasCustomWidths ? `calc(${width} - ${gapAdjustment}px)` : '1fr';
            })
            .join(' '),
          gap: `${gapSize}px`,
          width: '100%',
          boxSizing: 'border-box',
          gridAutoRows: 'min-content',
          backgroundColor: block.styles.backgroundColor || 'transparent',
        }}
      >
        {Array.from({ length: columnCount }).map((_, index) => {
          return (
            <div
              key={index}
              data-column-index={index}
              className="relative flex flex-col"
              style={{
                boxSizing: 'border-box',
              }}
            >
              <ColumnDropZoneWrapper
                blockId={block.id}
                columnIndex={index}
                columnContent={columnContent[index]}
                renderContent={renderColumnContentPreview}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const getHeadingStyles = () => {
    const headingStyles = {
      padding: computePadding(block.styles, '20px'),
      fontSize: block.styles.fontSize || (isHeadingBlock ? '32px' : '16px'),
      fontWeight: block.styles.fontWeight || (isHeadingBlock ? '700' : '400'),
      fontStyle: block.styles.fontStyle || 'normal',
      color: block.styles.color || '#111827',
      textAlign: block.styles.textAlign || 'left',
      lineHeight: block.styles.lineHeight || (isHeadingBlock ? '140%' : '160%'),
      letterSpacing: block.styles.letterSpacing || '0px',
      fontFamily: block.styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
      textDecoration: block.styles.textDecoration || 'none',
      backgroundColor: block.styles.backgroundColor || 'transparent',
    };
    return headingStyles;
  };

  const renderRichTextToolbar = () => {
    if (!isSelected || isPreview || !isRichTextBlock || isLocked) return null;

    const baseButtonClasses =
      'w-8 h-8 flex items-center justify-center rounded-md border text-sm font-semibold transition-colors';
    const getButtonClasses = (active?: boolean, disabled?: boolean) => {
      if (disabled) {
        return `${baseButtonClasses} bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed`;
      }
      return active
        ? `${baseButtonClasses} bg-ocean-50 text-ocean-600 border-ocean-200`
        : `${baseButtonClasses} bg-white text-slate-600 border-slate-200 hover:bg-slate-50`;
    };

    const activeListType = isTextBlock ? block.content.listType : undefined;

    return (
      <div
        ref={headingToolbarRef}
        className="pointer-events-auto absolute top-full left-1/2 -translate-x-1/2 mt-2 z-[100]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-nowrap items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1.5 shadow-lg w-max whitespace-nowrap">
          <button
            type="button"
            className={getButtonClasses(isHeadingBold)}
            title="Bold"
            onMouseDown={handleRichTextToolbarMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              if (isTextBlock) {
                requestInlineHtmlFormat('strong');
              } else {
                toggleHeadingBold();
              }
            }}
          >
            B
          </button>
          <button
            type="button"
            className={getButtonClasses(isHeadingItalic)}
            title="Italic"
            onMouseDown={handleRichTextToolbarMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              if (isTextBlock) {
                requestInlineHtmlFormat('em');
              } else {
                toggleHeadingItalic();
              }
            }}
          >
            <span className="italic">I</span>
          </button>
          <button
            type="button"
            className={getButtonClasses(hasHeadingUnderline)}
            title="Underline"
            onMouseDown={handleRichTextToolbarMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              if (isTextBlock) {
                requestInlineHtmlFormat('u');
              } else {
                toggleHeadingDecoration('underline');
              }
            }}
          >
            <span className="underline decoration-2">U</span>
          </button>
          <button
            type="button"
            className={getButtonClasses(hasHeadingStrikethrough)}
            title="Strikethrough"
            onMouseDown={handleRichTextToolbarMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              if (isTextBlock) {
                requestInlineHtmlFormat('s');
              } else {
                toggleHeadingDecoration('line-through');
              }
            }}
          >
            <span className="line-through decoration-2">S</span>
          </button>
          <div className="shrink-0 w-px h-6 bg-slate-200 mx-0.5" />
          {isTextBlock && (
            <>
              <button
                type="button"
                className={getButtonClasses(activeListType === 'unordered')}
                title="Bulleted list"
                onMouseDown={handleRichTextToolbarMouseDown}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTextListType('unordered');
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="6" cy="6" r="1.25" />
                  <circle cx="6" cy="12" r="1.25" />
                  <circle cx="6" cy="18" r="1.25" />
                  <line x1="10" y1="6" x2="20" y2="6" strokeWidth={1.5} strokeLinecap="round" />
                  <line x1="10" y1="12" x2="20" y2="12" strokeWidth={1.5} strokeLinecap="round" />
                  <line x1="10" y1="18" x2="20" y2="18" strokeWidth={1.5} strokeLinecap="round" />
                </svg>
              </button>
              <button
                type="button"
                className={getButtonClasses(activeListType === 'ordered')}
                title="Numbered list"
                onMouseDown={handleRichTextToolbarMouseDown}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTextListType('ordered');
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <text x="4" y="7" fontSize="4" fontWeight="bold">
                    1
                  </text>
                  <text x="4" y="13" fontSize="4" fontWeight="bold">
                    2
                  </text>
                  <text x="4" y="19" fontSize="4" fontWeight="bold">
                    3
                  </text>
                  <line x1="10" y1="6" x2="20" y2="6" strokeWidth={1.5} strokeLinecap="round" />
                  <line x1="10" y1="12" x2="20" y2="12" strokeWidth={1.5} strokeLinecap="round" />
                  <line x1="10" y1="18" x2="20" y2="18" strokeWidth={1.5} strokeLinecap="round" />
                </svg>
              </button>
              <div className="shrink-0 w-px h-6 bg-slate-200 mx-0.5" />
            </>
          )}
          <button
            type="button"
            className={getButtonClasses(headingHasLink)}
            title="Add link"
            onMouseDown={handleRichTextToolbarMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              handleHeadingLinkPrompt();
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 13a5 5 0 007.07 0l1.41-1.41a5 5 0 00-7.07-7.07L10 5"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 11a5 5 0 00-7.07 0L5.5 12.43a5 5 0 107.07 7.07L14 18.5"
              />
            </svg>
          </button>
          <button
            type="button"
            className={getButtonClasses(false, !headingHasLink)}
            title="Remove link"
            disabled={!headingHasLink}
            onMouseDown={handleRichTextToolbarMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveHeadingLink();
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 13a5 5 0 007.07 0l1.41-1.41a5 5 0 00-7.07-7.07L10 5"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 11a5 5 0 00-7.07 0L5.5 12.43a5 5 0 107.07 7.07L14 18.5"
              />
              <line x1="4" y1="4" x2="20" y2="20" strokeWidth={2} />
            </svg>
          </button>
          <div className="relative shrink-0">
            <button
              type="button"
              className={getButtonClasses(showTextColorPicker)}
              title="Text color"
              onMouseDown={handleRichTextToolbarMouseDown}
              onClick={(e) => {
                e.stopPropagation();
                // Capture current selection when opening color picker
                if (!showTextColorPicker && inlineHeadingTextareaRef.current) {
                  const textarea = inlineHeadingTextareaRef.current;
                  headingSelectionRangeRef.current = {
                    start: textarea.selectionStart ?? 0,
                    end: textarea.selectionEnd ?? 0,
                  };
                }
                setShowTextColorPicker((prev) => !prev);
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                />
              </svg>
            </button>
            {showTextColorPicker && (() => {
              const textValue = headingTextValue;
              const textLength = textValue.length;
              const { start, end } = headingSelectionRangeRef.current;
              const hasSelection = start !== end && textLength > 0;
              const selectedText = hasSelection ? textValue.slice(start, end) : '';

              return (
                <div
                  className="absolute top-11 right-0 z-[60] bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-56"
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                    Text color
                  </p>
                  {!hasSelection && (
                    <p className="text-xs text-amber-600 mb-2 px-2 py-1 bg-amber-50 rounded border border-amber-200">
                      Select text to change color
                    </p>
                  )}
                  {hasSelection && (
                    <p className="text-xs text-slate-500 mb-2 truncate" title={selectedText}>
                      Selected: "{selectedText.length > 20 ? selectedText.slice(0, 20) + '...' : selectedText}"
                    </p>
                  )}
                  <div className="grid grid-cols-7 gap-1.5 mb-3">
                    {[
                      '#111827', '#6B7280', '#000000', '#EF4444', '#F97316', '#F59E0B', '#10B981',
                      '#14B8A6', '#0EA5E9', '#2563EB', '#6366F1', '#8B5CF6', '#EC4899', '#F43F5E',
                    ].map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-6 h-6 rounded-full border border-slate-300 transition ${hasSelection
                          ? 'hover:border-slate-500 cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                          }`}
                        style={{ backgroundColor: color }}
                        onClick={() => hasSelection && applyInlineTextColor(color)}
                        title={color}
                        disabled={!hasSelection}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={customTextColor}
                      onChange={(e) => setCustomTextColor(e.target.value)}
                      className="w-8 h-8 border border-slate-300 rounded-md cursor-pointer"
                      disabled={!hasSelection}
                    />
                    <input
                      type="text"
                      value={customTextColor}
                      onChange={(e) => setCustomTextColor(e.target.value)}
                      className="flex-1 border border-slate-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!hasSelection}
                    />
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-md text-white text-xs font-semibold ${hasSelection
                        ? 'bg-slate-900 hover:bg-slate-800 cursor-pointer'
                        : 'bg-slate-400 cursor-not-allowed'
                        }`}
                      onClick={() => hasSelection && applyInlineTextColor(customTextColor)}
                      disabled={!hasSelection}
                    >
                      Apply
                    </button>
                  </div>
                  <button
                    type="button"
                    className={`mt-2 text-xs font-semibold ${hasSelection
                      ? 'text-slate-600 hover:text-slate-900'
                      : 'text-slate-400 cursor-not-allowed'
                      }`}
                    onClick={() => hasSelection && applyInlineTextColor(null)}
                    disabled={!hasSelection}
                  >
                    Reset color
                  </button>
                </div>
              );
            })()}
          </div>
          <div className="shrink-0 w-px h-6 bg-slate-200 mx-0.5" />
          <div className="relative shrink-0">
            <button
              type="button"
              className={getButtonClasses(showHeadingEmojiDropdown)}
              title="Insert emoji"
              onMouseDown={handleRichTextToolbarMouseDown}
              onClick={(e) => {
                e.stopPropagation();
                setShowHeadingEmojiDropdown((prev) => !prev);
              }}
            >
              🙂
            </button>
            {showHeadingEmojiDropdown && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-64 z-[60]">
                <input
                  type="text"
                  value={headingEmojiSearch}
                  onChange={(e) => setHeadingEmojiSearch(e.target.value)}
                  placeholder="Search emojis..."
                  className="w-full mb-2 px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-ocean-500"
                />
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
                  {filteredHeadingEmojis.length === 0 && (
                    <div className="text-xs text-slate-500">No matches found</div>
                  )}
                  {filteredHeadingEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="text-xl hover:scale-110 transition-transform"
                      onClick={(e) => {
                        e.stopPropagation();
                        insertHeadingEmoji(emoji);
                        setHeadingEmojiSearch('');
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="relative shrink-0">
            <button
              type="button"
              className={`h-8 px-3 rounded-md border text-xs font-semibold flex items-center gap-1.5 transition-colors whitespace-nowrap ${showHeadingMergeDropdown
                ? 'bg-ocean-50 text-ocean-600 border-ocean-200'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              onMouseDown={handleRichTextToolbarMouseDown}
              onClick={(e) => {
                e.stopPropagation();
                setShowHeadingMergeDropdown((prev) => !prev);
              }}
            >
              Merge Tags
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showHeadingMergeDropdown && (
              <div className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-xl w-56 z-[60] max-h-60 overflow-y-auto">
                {DEFAULT_HEADING_MERGE_TAGS.map((tag) => (
                  <button
                    key={tag.mergeTag}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-slate-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      insertHeadingMergeTag(tag.mergeTag);
                    }}
                  >
                    <div className="text-sm font-medium text-slate-700">{tag.label}</div>
                    <div className="text-xs text-slate-500">{tag.mergeTag}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderHeadingBlock = () => {
    const level = block.content.level || 1;
    const HeadingTag = (`h${level}` as keyof React.JSX.IntrinsicElements) || 'h1';
    const headingStyles = getHeadingStyles();
    const headingTextAlign = headingStyles.textAlign as React.CSSProperties['textAlign'];
    const headingWrapperStyleBase: React.CSSProperties = {
      width: '100%',
      boxSizing: 'border-box',
      padding: headingStyles.padding,
      backgroundColor: headingStyles.backgroundColor,
      textAlign: headingTextAlign,
    };
    const headingWrapperStyle = isInlineEditingHeading
      ? { ...headingWrapperStyleBase, padding: 0 }
      : headingWrapperStyleBase;

    const headingContent = headingTextValue || 'Heading';
    const superscriptFontSize = scaleFontSize(headingStyles.fontSize, 0.75);
    const subscriptFontSize = scaleFontSize(headingStyles.fontSize, 0.8);

    const ensureUnderline = (decoration?: string) => {
      if (!decoration) return 'underline';
      const tokens = decoration
        .split(/\s+/)
        .map((token) => token.trim())
        .filter(Boolean);
      if (!tokens.includes('underline')) {
        tokens.push('underline');
      }
      return tokens.join(' ');
    };

    const linkedTextDecoration = ensureUnderline(headingStyles.textDecoration);

    const renderLinkedContent = () => {
      if (!headingContent.length) return null;
      const boundaries = new Set<number>([0, headingContent.length]);
      if (headingHasLink && headingLinkRangeEnd > headingLinkRangeStart) {
        boundaries.add(headingLinkRangeStart);
        boundaries.add(headingLinkRangeEnd);
      }
      headingColorRanges.forEach((range) => {
        const start = Math.max(0, Math.min(range.start, headingContent.length));
        const end = Math.max(start, Math.min(range.end, headingContent.length));
        if (end > start) {
          boundaries.add(start);
          boundaries.add(end);
        }
      });
      const sorted = Array.from(boundaries).sort((a, b) => a - b);
      const nodes: React.ReactNode[] = [];
      for (let i = 0; i < sorted.length - 1; i += 1) {
        const segStart = sorted[i];
        const segEnd = sorted[i + 1];
        if (segEnd <= segStart) continue;
        const segmentText = headingContent.slice(segStart, segEnd);
        if (!segmentText) continue;
        const colorValue = headingColorRanges.find(
          (range) => segStart >= range.start && segStart < range.end
        )?.color;
        const isLinkSegment =
          headingHasLink &&
          segEnd > headingLinkRangeStart &&
          segStart < headingLinkRangeEnd;
        let chunkNode: React.ReactNode = segmentText;
        if (isLinkSegment) {
          chunkNode = (
            <a
              key={`heading-link-${segStart}-${segEnd}`}
              href={block.content.linkUrl}
              target={headingLinkTarget}
              rel="noopener noreferrer"
              onClick={(event) => event.preventDefault()}
              className="inline-block"
              style={{
                color: colorValue || headingStyles.color,
                textDecoration: linkedTextDecoration,
              }}
            >
              {segmentText || block.content.linkUrl}
            </a>
          );
        } else if (colorValue) {
          chunkNode = (
            <span style={{ color: colorValue }} key={`heading-color-${segStart}-${segEnd}`}>
              {segmentText}
            </span>
          );
        }
        nodes.push(
          <React.Fragment key={`heading-seg-${segStart}-${segEnd}`}>
            {chunkNode}
          </React.Fragment>
        );
      }
      return nodes.length ? nodes : headingContent;
    };

    const renderSupSubContent = () => {
      const contentNode = renderLinkedContent();
      if (isHeadingSuperscript) {
        return (
          <sup
            style={{
              fontSize: superscriptFontSize || '0.75em',
              lineHeight: '1',
              display: 'inline-block',
              transform: 'translateY(-0.35em)',
            }}
          >
            {contentNode}
          </sup>
        );
      }
      if (isHeadingSubscript) {
        return (
          <sub
            style={{
              fontSize: subscriptFontSize || '0.8em',
              lineHeight: '1',
              display: 'inline-block',
              transform: 'translateY(0.2em)',
            }}
          >
            {contentNode}
          </sub>
        );
      }
      return contentNode;
    };

    const headingProps = {
      style: {
        ...headingStyles,
        display: 'block',
        width: '100%',
        boxSizing: 'border-box',
        padding: 0,
        margin: 0,
        backgroundColor: 'transparent',
        wordWrap: 'break-word' as React.CSSProperties['wordWrap'],
        overflowWrap: 'break-word' as React.CSSProperties['overflowWrap'],
        maxWidth: '100%',
        whiteSpace: 'pre-wrap' as React.CSSProperties['whiteSpace'],
      } satisfies React.CSSProperties,
      onClick: handleHeadingClick,
    };

    if (isInlineEditingHeading) {
      return (
        <div
          style={headingWrapperStyle}
          className={`relative ${isSelected && !isPreview ? 'pb-16' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {renderRichTextToolbar()}
          <HeadingTag
            {...headingProps}
            style={{
              ...headingProps.style,
              padding: headingStyles.padding,
              color: 'transparent',
              visibility: 'hidden',
              pointerEvents: 'none',
              whiteSpace: 'pre-wrap',
            } satisfies React.CSSProperties}
            aria-hidden="true"
          >
            {inlineHeadingValue || 'Heading'}
          </HeadingTag>
          <textarea
            ref={inlineHeadingTextareaRef}
            value={inlineHeadingValue}
            onChange={(e) => setInlineHeadingValue(e.target.value)}
            onBlur={commitHeadingChange}
            onKeyDown={handleHeadingKeyDown}
            onSelect={(e) => {
              const target = e.currentTarget;
              headingSelectionRangeRef.current = {
                start: target.selectionStart ?? 0,
                end: target.selectionEnd ?? 0,
              };
            }}
            onMouseUp={(e) => {
              // Preserve selection on mouse up (after double-click)
              const target = e.currentTarget;
              headingSelectionRangeRef.current = {
                start: target.selectionStart ?? 0,
                end: target.selectionEnd ?? 0,
              };
            }}
            className="absolute inset-0 w-full h-full bg-transparent border-0 focus:outline-none focus:ring-0 resize-none"
            style={{
              fontSize:
                isHeadingSuperscript || isHeadingSubscript
                  ? (isHeadingSuperscript ? superscriptFontSize : subscriptFontSize) || headingStyles.fontSize
                  : headingStyles.fontSize,
              fontWeight: headingStyles.fontWeight,
              fontStyle: headingStyles.fontStyle,
              fontFamily: headingStyles.fontFamily,
              lineHeight: headingStyles.lineHeight,
              letterSpacing: headingStyles.letterSpacing,
              color: headingStyles.color,
              textDecoration: headingStyles.textDecoration,
              textAlign: headingTextAlign,
              padding: headingStyles.padding,
              margin: 0,
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
              whiteSpace: 'pre-wrap',
              transform: isHeadingSuperscript
                ? 'translateY(-0.35em)'
                : isHeadingSubscript
                  ? 'translateY(0.2em)'
                  : undefined,
            }}
            rows={Math.max(1, inlineHeadingValue.split('\n').length)}
          />
        </div>
      );
    }

    const headingContentNode = renderSupSubContent();

    return (
      <div
        style={headingWrapperStyle}
        className={`relative ${isSelected && !isPreview ? 'pb-16' : ''}`}
      >
        {renderRichTextToolbar()}
        <HeadingTag {...headingProps}>{headingContentNode}</HeadingTag>
      </div>
    );
  };

  const renderTextBlock = () => {
    if (!isTextBlock) return null;
    const textStyles = getHeadingStyles();
    const textAlign = textStyles.textAlign as React.CSSProperties['textAlign'];
    const wrapperStyle: React.CSSProperties = {
      padding: textStyles.padding,
      backgroundColor: textStyles.backgroundColor,
      textAlign,
    };
    const textContent = headingTextValue || 'Text content';
    const listType = block.content.listType as 'unordered' | 'ordered' | undefined;
    const handleTextBlockClick = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      if (!isSelected) {
        onSelect();
        return;
      }
      if (!isInlineEditingHeading && !isLocked) {
        setIsInlineEditingHeading(true);
      }
    };

    const renderSegment = (start: number, end: number): React.ReactNode => {
      if (start >= end) return null;
      const boundaries = new Set<number>([start, end]);
      if (headingHasLink && headingLinkRangeEnd > headingLinkRangeStart) {
        const linkStart = Math.max(start, Math.min(headingLinkRangeStart, end));
        const linkEnd = Math.max(linkStart, Math.min(headingLinkRangeEnd, end));
        if (linkEnd > linkStart) {
          boundaries.add(linkStart);
          boundaries.add(linkEnd);
        }
      }
      headingColorRanges.forEach((range) => {
        const rangeStart = Math.max(start, Math.min(range.start, end));
        const rangeEnd = Math.max(rangeStart, Math.min(range.end, end));
        if (rangeEnd > rangeStart) {
          boundaries.add(rangeStart);
          boundaries.add(rangeEnd);
        }
      });
      const sorted = Array.from(boundaries).sort((a, b) => a - b);
      const nodes: React.ReactNode[] = [];
      for (let i = 0; i < sorted.length - 1; i += 1) {
        const segStart = sorted[i];
        const segEnd = sorted[i + 1];
        if (segEnd <= segStart) continue;
        const segmentText = textContent.slice(segStart, segEnd);
        if (!segmentText) continue;
        const appliedColor = headingColorRanges.find(
          (range) => segStart >= range.start && segStart < range.end
        )?.color;
        const isLinkSegment =
          headingHasLink &&
          segEnd > headingLinkRangeStart &&
          segStart < headingLinkRangeEnd;
        let chunkNode: React.ReactNode = segmentText;
        if (isLinkSegment) {
          chunkNode = (
            <a
              key={`link-${segStart}-${segEnd}`}
              href={block.content.linkUrl}
              target={headingLinkTarget}
              rel="noopener noreferrer"
              onClick={(event) => event.preventDefault()}
              style={{
                color: appliedColor || textStyles.color,
                textDecoration: textStyles.textDecoration || 'underline',
              }}
            >
              {segmentText || block.content.linkUrl}
            </a>
          );
        } else if (appliedColor) {
          chunkNode = (
            <span style={{ color: appliedColor }} key={`color-${segStart}-${segEnd}`}>
              {segmentText}
            </span>
          );
        }
        nodes.push(
          <React.Fragment key={`seg-${segStart}-${segEnd}-${appliedColor || 'base'}`}>
            {chunkNode}
          </React.Fragment>
        );
      }
      if (!nodes.length) {
        return textContent.slice(start, end) || '\u00A0';
      }
      return nodes;
    };

    const commonTextStyles: React.CSSProperties = {
      fontSize: textStyles.fontSize,
      fontWeight: textStyles.fontWeight,
      fontStyle: textStyles.fontStyle,
      color: textStyles.color,
      lineHeight: textStyles.lineHeight,
      letterSpacing: textStyles.letterSpacing,
      fontFamily: textStyles.fontFamily,
      textDecoration: textStyles.textDecoration,
      textAlign: textAlign,
      margin: 0,
    };

    const renderDisplayContent = () => {
      if (block.content.isHtml && !listType) {
        return (
          <div
            style={{
              ...commonTextStyles,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}
            dangerouslySetInnerHTML={{ __html: textContent }}
          />
        );
      }

      if (listType === 'unordered' || listType === 'ordered') {
        const lines = textContent.split('\n');
        let cursor = 0;
        const ListTag = (listType === 'ordered' ? 'ol' : 'ul') as keyof React.JSX.IntrinsicElements;
        const listStyleType = listType === 'ordered' ? 'decimal' : 'disc';
        return (
          <ListTag
            style={{
              ...commonTextStyles,
              whiteSpace: 'normal',
              paddingLeft: '1.5rem',
              listStylePosition: 'outside',
              listStyleType,
            }}
          >
            {lines.map((line: string, index: number) => {
              const start = cursor;
              const end = start + line.length;
              cursor = end + 1;
              return (
                <li key={`${line}-${index}`} style={{ marginBottom: '0.35em' }}>
                  {renderSegment(start, end)}
                </li>
              );
            })}
          </ListTag>
        );
      }

      return (
        <div
          style={{
            ...commonTextStyles,
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {renderSegment(0, textContent.length)}
        </div>
      );
    };

    if (isInlineEditingHeading) {
      return (
        <div
          style={{ ...wrapperStyle, padding: 0 }}
          className={`relative ${isSelected && !isPreview ? 'pb-16' : ''}`}
          onClick={handleTextBlockClick}
        >
          {renderRichTextToolbar()}
          {/* Ghost element to maintain layout height during editing */}
          <div
            style={{
              ...commonTextStyles,
              padding: textStyles.padding,
              visibility: 'hidden',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              minHeight: '3em', // Matches the default 3 rows of the textarea
            }}
            aria-hidden="true"
          >
            {inlineHeadingValue || 'Text content'}
          </div>
          <textarea
            ref={inlineHeadingTextareaRef}
            value={inlineHeadingValue}
            onChange={(e) => setInlineHeadingValue(e.target.value)}
            onBlur={commitHeadingChange}
            onKeyDown={handleHeadingKeyDown}
            onSelect={(e) => {
              const target = e.currentTarget;
              headingSelectionRangeRef.current = {
                start: target.selectionStart ?? 0,
                end: target.selectionEnd ?? 0,
              };
            }}
            onMouseUp={(e) => {
              const target = e.currentTarget;
              headingSelectionRangeRef.current = {
                start: target.selectionStart ?? 0,
                end: target.selectionEnd ?? 0,
              };
            }}
            className="absolute inset-0 w-full h-full bg-transparent border-0 focus:outline-none focus:ring-0 resize-none"
            style={{
              ...commonTextStyles,
              padding: textStyles.padding,
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
            }}
            rows={Math.max(3, inlineHeadingValue.split('\n').length)}
          />
        </div>
      );
    }

    return (
      <div
        style={wrapperStyle}
        className={`relative ${isSelected && !isPreview ? 'pb-16' : ''}`}
        onClick={handleTextBlockClick}
      >
        {renderRichTextToolbar()}
        {renderDisplayContent()}
      </div>
    );
  };

  const renderListBlock = () => {
    const items = Array.isArray(block.content.items)
      ? block.content.items
      : (typeof block.content.text === 'string' && block.content.text.trim()
        ? block.content.text.split('\n').filter((line: string) => line.trim())
        : ['List item 1', 'List item 2', 'List item 3']);

    const listType = block.content.listType === 'number' || block.content.listType === 'ordered' ? 'ordered' : (block.content.listType === 'check' ? 'check' : (block.content.listType === 'none' ? 'none' : 'unordered'));
    const ListComponent = listType === 'ordered' ? 'ol' : 'ul';
    const textAlign = block.styles.textAlign || 'left';
    const isCentered = textAlign === 'center';
    const isRight = textAlign === 'right';

    return (
      <div
        className="relative"
        style={{
          padding: computePadding(block.styles, '20px'),
          backgroundColor: block.styles.backgroundColor || 'transparent',
        }}
      >
        <ListComponent
          style={{
            margin: 0,
            padding: 0,
            listStyleType: 'none',
            fontSize: block.styles.fontSize || '16px',
            color: block.styles.color || '#374151',
            textAlign: textAlign,
            lineHeight: block.styles.lineHeight || '160%',
            fontFamily: block.styles.fontFamily || globalFontFamily || 'Arial, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isCentered ? 'center' : (isRight ? 'flex-end' : 'flex-start'),
          }}
        >
          {items.map((item: string, index: number) => (
            <li
              key={index}
              style={{
                marginBottom: block.styles.listItemSpacing || '12px',
                position: 'relative',
                minHeight: '24px',
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: isCentered ? 'center' : (isRight ? 'flex-end' : 'flex-start'),
                width: '100%',
              }}
              className="group/item"
            >
              {listType !== 'none' && (
                <div
                  style={{
                    width: '40px',
                    textAlign: 'right',
                    paddingRight: '12px',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'flex-end',
                    color: block.styles.markerColor || block.styles.color || '#94A3B8',
                    fontSize: '0.9em',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {listType === 'check' ? (
                    <span style={{ color: block.styles.markerColor || block.styles.color || '#000', fontWeight: 'bold' }}>✓</span>
                  ) : listType === 'ordered' ? (
                    <span>{index + 1}.</span>
                  ) : (
                    <span>•</span>
                  )}
                </div>
              )}

              <input
                className={`bg-transparent border-0 focus:outline-none focus:ring-0 p-0 m-0 ${isCentered ? 'w-auto min-w-[50px] text-center' : 'flex-1 w-full'}`}
                value={item}
                onChange={(e) => updateListItem(index, e.target.value)}
                placeholder="List item..."
                style={{
                  textAlign: textAlign as any,
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addListItem();
                  }
                  if (e.key === 'Backspace' && !item && items.length > 1) {
                    e.preventDefault();
                    removeListItem(index);
                  }
                }}
              />

              {isSelected && items.length > 1 && (
                <button
                  className="ml-2 p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover/item:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeListItem(index);
                  }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ListComponent>

        {isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              addListItem();
            }}
            className={`mt-2 flex items-center gap-1.5 text-xs font-semibold text-ocean-600 hover:text-ocean-700 transition-colors px-1 ${isCentered ? 'ml-auto mr-auto w-fit' : (isRight ? 'ml-auto' : '')}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Item
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Special Image Control Bar - Removed Add Image and Add Link buttons */}

      <div
        ref={(node) => {
          setNodeRef(node);
          blockRef.current = node;
        }}
        style={style}
        className={`email-block relative group m-0 p-0 ${isDragging ? 'z-[60]' : isSelected ? 'z-[40]' : hasValidationError ? 'z-[20]' : ''}`}
        onClick={onSelect}
        onContextMenu={(e) => {
          if (isPreview) return;
          e.preventDefault();
          e.stopPropagation();
          onSelect();
          setContextMenu({ x: e.clientX, y: e.clientY });
        }}
      >
        {/* Unified Precision Border Overlay */}
        {!isPreview && (
          <div 
            className={`absolute inset-0 pointer-events-none rounded-none transition-colors z-10 ${
              hasValidationError 
                ? 'border-2 border-rose-500 bg-rose-50/10' 
                : isSelected 
                  ? 'border-2 border-ocean-500' 
                  : 'border-2 border-dashed border-transparent group-hover:border-slate-300'
            }`} 
          />
        )}

        {/* Plus Buttons - Top and Bottom */}
        {isSelected && !isPreview && onInsertBlock && (
          <>
            {/* Top Plus Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowComponentPicker(showComponentPicker === 'top' ? null : 'top');
              }}
              className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-ocean-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-ocean-600 transition-colors z-20"
              title="Add component above"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* Bottom Plus Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowComponentPicker(showComponentPicker === 'bottom' ? null : 'bottom');
              }}
              className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-ocean-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-ocean-600 transition-colors z-20"
              title="Add component below"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* Component Picker - Modern Draggable Design */}
            {showComponentPicker && (
              <div
                ref={pickerRef}
                className="fixed z-50"
                style={{
                  left: pickerPosition?.x ?? '50%',
                  top: pickerPosition?.y ?? (showComponentPicker === 'top' ? '30%' : '40%'),
                  transform: pickerPosition ? 'none' : 'translateX(-50%)',
                  willChange: isDraggingPicker ? 'left, top' : 'auto',
                  transition: isDraggingPicker ? 'none' : 'left 0.15s ease-out, top 0.15s ease-out',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-2xl shadow-[0_20px_70px_-15px_rgba(0,0,0,0.3)] w-[300px] overflow-hidden transition-shadow ${isDraggingPicker ? 'shadow-[0_25px_80px_-10px_rgba(0,0,0,0.4)]' : ''}`}>
                  {/* Draggable Header */}
                  <div
                    className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 py-3 cursor-move select-none"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setIsDraggingPicker(true);
                      const rect = pickerRef.current?.getBoundingClientRect();
                      if (rect) {
                        pickerDragOffset.current = {
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                        };
                      }
                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        setPickerPosition({
                          x: moveEvent.clientX - pickerDragOffset.current.x,
                          y: moveEvent.clientY - pickerDragOffset.current.y,
                        });
                      };
                      const handleMouseUp = () => {
                        setIsDraggingPicker(false);
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  >
                    <div className="text-xs font-semibold text-white/90 uppercase tracking-wider flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-3.5 h-3.5 text-ocean-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add {showComponentPicker === 'top' ? 'Above' : 'Below'}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Drag indicator */}
                        <svg className="w-4 h-4 text-white/40" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                        </svg>
                        {/* Close button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowComponentPicker(null);
                            setPickerPosition(null);
                          }}
                          className="text-white/60 hover:text-white transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Scrollable grid */}
                  <div className="max-h-[260px] overflow-y-auto p-3 bg-gradient-to-b from-slate-50/50 to-white">
                    <div className="grid grid-cols-3 gap-2">
                      {BLOCK_TYPES.map((blockType) => {
                        const Icon = blockType.icon;
                        return (
                          <button
                            key={blockType.type}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onInsertBlock && showComponentPicker) {
                                const position = showComponentPicker === 'top' ? 'above' : 'below';
                                onInsertBlock(block.id, blockType.type, position);
                                setShowComponentPicker(null);
                                setPickerPosition(null);
                              }
                            }}
                            className="group flex flex-col items-center justify-center p-2.5 bg-white border border-slate-200/80 rounded-xl hover:border-ocean-400 hover:shadow-lg hover:shadow-ocean-500/10 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                          >
                            <div className="w-8 h-8 flex items-center justify-center mb-1.5 rounded-lg bg-slate-100 group-hover:bg-ocean-50 transition-colors">
                              <Icon className="w-4 h-4 text-slate-500 group-hover:text-ocean-600 transition-colors" />
                            </div>
                            <div className="text-[10px] font-medium text-slate-600 group-hover:text-slate-900 text-center leading-tight transition-colors">
                              {blockType.label}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Block Controls - Vertical - Positioned to the left, centered vertically */}
        {isSelected && (
          <div className="absolute -left-14 top-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5 bg-slate-900 text-white rounded-lg px-1 py-1.5 text-xs z-10 shadow-lg">
            {/* Move Up Button */}
            {onMoveUp && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp(block.id);
                }}
                disabled={!canMoveUp}
                className={`p-2 rounded transition-colors ${canMoveUp
                  ? 'hover:bg-slate-800 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed'
                  }`}
                title="Move Up"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </button>
            )}
            {/* Move Down Button */}
            {onMoveDown && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown(block.id);
                }}
                disabled={!canMoveDown}
                className={`p-2 rounded transition-colors ${canMoveDown
                  ? 'hover:bg-slate-800 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed'
                  }`}
                title="Move Down"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(block.id);
              }}
              className="hover:bg-slate-800 p-2 rounded"
              title="Duplicate"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!isLocked) {
                  onDelete(block.id);
                }
              }}
              className={`p-2 rounded ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-600'}`}
              title={isLocked ? 'Upgrade to Pro to remove Scubamail branding' : 'Delete'}
              disabled={isLocked}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
            {/* Save as Design Button */}
            {onSaveBlock && (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSavePopup(true);
                    setSaveBlockName('');
                  }}
                  className="hover:bg-slate-800 p-2 rounded"
                  title="Save as Design"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z"
                    />
                  </svg>
                </button>
                {/* Save Popup */}
                {showSavePopup && (
                  <div
                    ref={savePopupRef}
                    className="absolute left-full ml-2 top-0 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-50 min-w-[200px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="text"
                      value={saveBlockName}
                      onChange={(e) => setSaveBlockName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && saveBlockName.trim()) {
                          onSaveBlock(block, saveBlockName.trim());
                          setShowSavePopup(false);
                          setSaveBlockName('');
                        }
                        if (e.key === 'Escape') {
                          setShowSavePopup(false);
                          setSaveBlockName('');
                        }
                      }}
                      placeholder="Enter block name"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
                      autoFocus
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => {
                          if (saveBlockName.trim() && onSaveBlock) {
                            onSaveBlock(block, saveBlockName.trim());
                            setShowSavePopup(false);
                            setSaveBlockName('');
                          }
                        }}
                        disabled={!saveBlockName.trim()}
                        className="flex-1 px-3 py-1.5 bg-ocean-500 text-white text-xs font-medium rounded-md hover:bg-ocean-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setShowSavePopup(false);
                          setSaveBlockName('');
                        }}
                        className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-center p-2 cursor-move">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8h16M4 16h16"
                />
              </svg>
            </div>
          </div>
        )}

        {/* Drag Handle Indicator */}
        {!isSelected && (
          <>
            {/* Block Type Label - attached to block, draggable */}
            <div
              className="absolute left-0 top-0 bg-blue-500 text-white text-xs font-medium px-2 py-1.5 rounded-l shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 cursor-grab active:cursor-grabbing hover:bg-blue-600"
              {...attributes}
              {...listeners}
              style={{
                transform: 'translateX(-100%)',
                borderTopRightRadius: '0',
                borderBottomRightRadius: '0',
                borderRight: '2px solid #3b82f6'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {BLOCK_TYPES.find(bt => bt.type === block.type)?.label || block.type}
            </div>
          </>
        )}

        {/* Block Content */}
        <div className="relative">
          {isColumnsBlock
            ? renderColumnsEditor()
            : isHeadingBlock
              ? renderHeadingBlock()
              : isListBlock
                ? renderListBlock()
                : isTextBlock
                  ? renderTextBlock()
                  : <BlockRenderer block={block} isMobile={isMobile} globalFontFamily={globalFontFamily} />}
        </div>
      </div>

      {showHeadingLinkModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 px-4">
          <div
            ref={headingLinkModalRef}
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl"
          >
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Insert/Edit Link</h3>
                <p className="text-sm text-slate-500">Attach a link to this content</p>
              </div>
              <button
                type="button"
                onClick={() => setShowHeadingLinkModal(false)}
                className="text-slate-500 hover:text-slate-900 rounded-full p-1 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Action Type</label>
                <div className="relative">
                  <select
                    value="open-website"
                    disabled
                    className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 cursor-not-allowed"
                  >
                    <option value="open-website">Open Website</option>
                  </select>
                  <svg className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">URL</label>
                <input
                  type="url"
                  value={headingLinkForm.url}
                  onChange={(e) => handleHeadingLinkFormChange('url', e.target.value)}
                  placeholder="https://example.com"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Target</label>
                <div className="relative">
                  <select
                    value={headingLinkForm.target}
                    onChange={(e) => handleHeadingLinkFormChange('target', e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm appearance-none focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500 outline-none"
                  >
                    <option value="_blank">New Tab</option>
                    <option value="_self">Same Window</option>
                  </select>
                  <svg className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setShowHeadingLinkModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleHeadingLinkSave}
                disabled={!isHeadingLinkFormValid}
                className={`px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm transition-colors ${isHeadingLinkFormValid ? 'bg-slate-900 hover:bg-slate-800' : 'bg-slate-400 cursor-not-allowed'
                  }`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Right-Click Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-[100] bg-white rounded-xl shadow-2xl border border-slate-200 py-2 min-w-[180px] overflow-hidden"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
            Block Actions
          </div>
          <button
            onClick={() => {
              onDuplicate(block.id);
              setContextMenu(null);
            }}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Duplicate
          </button>
          {onMoveUp && canMoveUp && (
            <button
              onClick={() => {
                onMoveUp(block.id);
                setContextMenu(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              Move Up
            </button>
          )}
          {onMoveDown && canMoveDown && (
            <button
              onClick={() => {
                onMoveDown(block.id);
                setContextMenu(null);
              }}
              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors"
            >
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Move Down
            </button>
          )}
          <div className="h-px bg-slate-100 my-1" />
          <button
            onClick={() => {
              if (!isLocked) {
                onDelete(block.id);
                setContextMenu(null);
              }
            }}
            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-3 transition-colors ${isLocked
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-red-600 hover:bg-red-50'
              }`}
            disabled={isLocked}
          >
            {isLocked ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Upgrade to Remove
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </>
            )}
          </button>
        </div>
      )}
    </>
  );
};

